(function(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.TMMRecentlySold = api;
})(typeof window !== 'undefined' ? window : globalThis, function() {
  'use strict';

  const TIME_ZONE = 'America/Chicago';
  const RETENTION_DAYS = 3;
  const DAY_MS = 24 * 60 * 60 * 1000;
  const chicagoDateFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  function fieldValue(value) {
    return value && typeof value === 'object' && 'value' in value ? value.value : value;
  }

  function statusFlag(value) {
    const normalized = fieldValue(value);
    return normalized === true || normalized === 'true' || normalized === '1';
  }

  function hasStatusValue(value) {
    const normalized = fieldValue(value);
    return normalized !== undefined && normalized !== null && normalized !== '';
  }

  function pad2(value) {
    return String(value).padStart(2, '0');
  }

  function validDateParts(year, month, day) {
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  }

  function parseDateOnly(value) {
    const input = String(value || '').trim();
    let match = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    let year;
    let month;
    let day;
    if (match) {
      year = Number(match[1]);
      month = Number(match[2]);
      day = Number(match[3]);
    } else {
      match = input.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!match) return null;
      month = Number(match[1]);
      day = Number(match[2]);
      year = Number(match[3]);
    }
    if (!validDateParts(year, month, day)) return null;
    return {
      year,
      month,
      day,
      iso: `${year}-${pad2(month)}-${pad2(day)}`,
      display: `${pad2(month)}/${pad2(day)}/${year}`,
      ordinal: Date.UTC(year, month - 1, day) / DAY_MS
    };
  }

  function chicagoDate(date = new Date()) {
    const parts = Object.fromEntries(
      chicagoDateFormatter.formatToParts(date)
        .filter(part => part.type !== 'literal')
        .map(part => [part.type, part.value])
    );
    return parseDateOnly(`${parts.year}-${parts.month}-${parts.day}`);
  }

  function metafieldValue(variant, keys) {
    for (const key of keys) {
      const candidates = [variant?.[key], variant?.metafields?.custom?.[key]];
      for (const candidate of candidates) {
        const value = fieldValue(candidate);
        if (value !== undefined && value !== null && String(value).trim() !== '') return String(value).trim();
      }
    }
    return '';
  }

  function titleSoldDate(variant) {
    const match = String(variant?.title || '').match(/\(Sold - (\d{2}\/\d{2}\/\d{4})\)/);
    return match ? match[1] : '';
  }

  function soldDateRecord(variant) {
    const metadataDate = metafieldValue(variant, ['admin_sold_date', 'sold_date', 'sold_at']);
    if (metadataDate) {
      const parsed = parseDateOnly(metadataDate.slice(0, 10));
      return parsed ? { ...parsed, source: 'metadata' } : null;
    }
    const parsed = parseDateOnly(titleSoldDate(variant));
    return parsed ? { ...parsed, source: 'title' } : null;
  }

  function hasAuthoritativeSoldStatus(variant) {
    return hasStatusValue(variant?.admin_sold) ||
      hasStatusValue(variant?.sold) ||
      hasStatusValue(variant?.metafields?.custom?.admin_sold);
  }

  function isMarkedSold(variant) {
    if (hasAuthoritativeSoldStatus(variant)) {
      return statusFlag(variant?.admin_sold) ||
        statusFlag(variant?.sold) ||
        statusFlag(variant?.metafields?.custom?.admin_sold);
    }
    return Boolean(titleSoldDate(variant));
  }

  function isInventoryUnavailable(variant) {
    const quantity = Number(variant?.inventory_quantity);
    const hasQuantity = variant?.inventory_quantity !== undefined &&
      variant?.inventory_quantity !== null && variant?.inventory_quantity !== '';
    return variant?.available === false || (hasQuantity && Number.isFinite(quantity) && quantity <= 0);
  }

  function isVariantSold(variant) {
    return isMarkedSold(variant) || isInventoryUnavailable(variant);
  }

  function isVariantHidden(variant) {
    return statusFlag(variant?.admin_hidden) ||
      statusFlag(variant?.hidden) ||
      statusFlag(variant?.metafields?.custom?.admin_hidden) ||
      / - Hidden$/.test(String(variant?.title || ''));
  }

  function isVariantAvailable(variant) {
    if (!variant || variant.available !== true || isMarkedSold(variant) || isVariantHidden(variant)) return false;
    const quantity = Number(variant.inventory_quantity);
    const hasQuantity = variant.inventory_quantity !== undefined &&
      variant.inventory_quantity !== null && variant.inventory_quantity !== '';
    return !(hasQuantity && Number.isFinite(quantity) && quantity <= 0);
  }

  function isRecentlySoldDate(record, now = new Date()) {
    const today = chicagoDate(now);
    if (!record || !today) return false;
    const ageInCalendarDays = today.ordinal - record.ordinal;
    return ageInCalendarDays >= 0 && ageInCalendarDays < RETENTION_DAYS;
  }

  function isVariantRecentlySold(variant, now = new Date()) {
    return !isVariantHidden(variant) && isMarkedSold(variant) && isRecentlySoldDate(soldDateRecord(variant), now);
  }

  function availableVisibleVariants(product) {
    return (product?.variants || []).filter(isVariantAvailable);
  }

  function availableVisibleVariantCount(product) {
    return availableVisibleVariants(product).length;
  }

  function hasRecentlySoldVariant(product, now = new Date()) {
    return (product?.variants || []).some(variant => isVariantRecentlySold(variant, now));
  }

  function latestSoldVariant(product, now = new Date(), recentOnly = false) {
    return (product?.variants || []).reduce((latest, variant) => {
      if (isVariantHidden(variant) || !isMarkedSold(variant)) return latest;
      const date = soldDateRecord(variant);
      if (!date || (recentOnly && !isRecentlySoldDate(date, now))) return latest;
      return !latest || date.ordinal > latest.ordinal ? { ...date, variant } : latest;
    }, null);
  }

  function isRecentlySoldEligibleProduct(product) {
    const type = String(product?.product_type || product?.type || '').toLowerCase();
    const tags = Array.isArray(product?.tags) ? product.tags.join(',').toLowerCase() : String(product?.tags || '').toLowerCase();
    return !type.includes('sealed') && !tags.split(',').some(tag => tag.trim() === 'sealed');
  }

  function shouldIncludeInCollection(product, now = new Date()) {
    return availableVisibleVariantCount(product) > 0 ||
      (isRecentlySoldEligibleProduct(product) && hasRecentlySoldVariant(product, now));
  }

  function shouldIncludeInSearch(product, now = new Date()) {
    return shouldIncludeInCollection(product, now);
  }

  return {
    TIME_ZONE,
    RETENTION_DAYS,
    parseDateOnly,
    chicagoDate,
    soldDateRecord,
    isMarkedSold,
    isInventoryUnavailable,
    isVariantSold,
    isVariantHidden,
    isVariantAvailable,
    isRecentlySoldDate,
    isVariantRecentlySold,
    availableVisibleVariants,
    availableVisibleVariantCount,
    hasRecentlySoldVariant,
    latestSoldVariant,
    isRecentlySoldEligibleProduct,
    shouldIncludeInCollection,
    shouldIncludeInSearch
  };
});
